import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";

export default function HeroCarousel({ slides, interval = 7000 }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const total = slides.length;

  const goTo = useCallback(
    (index) => setCurrent((index + total) % total),
    [total],
  );
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (paused || total < 2) return undefined;
    const timer = setTimeout(next, interval);
    return () => clearTimeout(timer);
  }, [current, paused, interval, next, total]);

  const handleAction = (target) => {
    if (target.startsWith("/")) {
      navigate(target);
      return;
    }
    document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="kn-intro kn-intro--dark kn-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carrusel"
    >
      <button
        type="button"
        className="kn-carousel-arrow kn-carousel-arrow--prev"
        onClick={prev}
        aria-label="Anterior"
      >
        <ChevronLeftIcon />
      </button>

      <div className="kn-container kn-carousel-track">
        {slides.map((slide, index) => (
          <div
            key={slide.headline}
            className={`kn-intro-grid kn-carousel-slide${index === current ? " is-active" : ""}`}
            aria-hidden={index !== current}
          >
            <div>
              <span className="kn-eyebrow">{slide.eyebrow}</span>
              <h1 className="kn-hero-title kn-carousel-title">
                {slide.headline}
              </h1>
              {slide.subtitle && (
                <p className="kn-carousel-subtitle">{slide.subtitle}</p>
              )}
              {slide.paragraphs.map((paragraph) => (
                <p key={paragraph} className="kn-carousel-text">
                  {paragraph}
                </p>
              ))}
              {slide.closing && (
                <p className="kn-carousel-closing">{slide.closing}</p>
              )}
              <div className="kn-hero-actions">
                <button
                  type="button"
                  className="kn-btn-primary"
                  tabIndex={index === current ? 0 : -1}
                  onClick={() => handleAction(slide.primary.target)}
                >
                  {slide.primary.label}
                </button>
                <button
                  type="button"
                  className="kn-btn-ghost-light"
                  tabIndex={index === current ? 0 : -1}
                  onClick={() => handleAction(slide.secondary.target)}
                >
                  {slide.secondary.label}
                </button>
              </div>
            </div>
            <figure className="kn-hero-media kn-carousel-media">
              <img src={slide.image} alt={slide.imageAlt} />
              <figcaption>
                <strong>{slide.caption}</strong>
                <span>{slide.captionText}</span>
              </figcaption>
            </figure>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="kn-carousel-arrow kn-carousel-arrow--next"
        onClick={next}
        aria-label="Siguiente"
      >
        <ChevronRightIcon />
      </button>

      <div className="kn-carousel-dots">
        {slides.map(({ headline }, index) => (
          <button
            key={headline}
            type="button"
            className={`kn-carousel-dot${index === current ? " is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Ir al slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
